<template>
  <v-container>
    <div>
      <settings-bar
        :parent="'missionForm'"
        :pathId="pathId"
        :width="'330'"
        :isMissionFormValid="validMissionInformations"
        :invalidGamesForms="invalidGamesForms"
        :invalidRewardsForms="invalidRewardsForms"
        :isNewMission="missionId ? false : true"
        @go-to-path="goToPath"
        @delete-mission="deleteMission"
        @change-lang-edition="changeLangEdition"
      />
    </div>

    <div
      class="stickyClass"
      v-if="langEdition !== locale && path.langsEdited.includes(locale)"
    >
      <v-flex layout column align-start>
        <!--v-btn :ripple="false" text color="white" class="text-none grey--text elevation-0">
          Edition en {{$t('languages.'+langEdition)}}
        </v-btn-->
        <v-tooltip top>
          <template v-slot:activator="{ on: on }">
            <v-btn
              small
              color="white"
              class="text-none primary--text"
              @click="changeHint()"
              v-on="on"
            >
              <v-icon color="primary" size="18" class="mr-2" v-if="showHint">
                mdi-eye-outline
              </v-icon>
              <v-icon color="primary" size="18" class="mr-2" v-else>
                mdi-eye-off-outline
              </v-icon>
              <span>
                {{ $t('languages.' + locale) }}
              </span>
            </v-btn>
          </template>
          <span v-if="showHint">
            {{ $tc('languageHint.hide', $t('languages.' + locale)) }}
          </span>
          <span v-else>
            {{ $tc('languageHint.show', $t('languages.' + locale)) }}
          </span>
        </v-tooltip>
      </v-flex>
    </div>

    <v-form v-model="validMissionForm" ref="missionForm">
      <v-layout justify-center align-center>
        <div>
          <!-- mission generic informations -->
          <v-card-title class="justify-center cardWidth">
            <h2 class="font-weight-light">
              {{ $t('missionForm.information') }}
            </h2>
          </v-card-title>

          <v-flex layout justify-center align-center>
            <v-card width="600px">
              <v-layout pa-4>
                <div>
                  <!--:class="[(langEdition!==path.langsEdited[0] ? 'readOnlyClass' : null)]"-->
                  <v-flex>
                    <image-input
                      :label="$t('missionForm.mainImage')"
                      :showHint="showHint"
                      :default="imageObject"
                      :langEdition="langEdition"
                      @change-image-input="updateImage"
                      required
                    />
                    <v-flex layout justify-center>
                      <span class="rulesText" v-if="!imageObject">
                        {{ rules.image }}
                      </span>
                    </v-flex>
                  </v-flex>
                </div>

                <v-layout align-center justify-center pl-4>
                  <v-flex>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="name[langEdition]"
                          :label="$t('missionForm.name') + ' *'"
                          v-on="on"
                          @blur="updateName"
                          :rules="rules.name"
                          :counter="maxCharName"
                          :hint="langEdition !== locale ? name[locale] : null"
                          :persistent-hint="showHint"
                        >
                          <language-translator
                            v-if="locale !== langEdition"
                            :langEdition="langEdition"
                            :textToTranslate="name[locale]"
                            :type="'name'"
                            @text-translated="updateTranslatedText"
                            slot="append"
                          >
                          </language-translator>
                        </v-text-field>
                      </template>
                      <span>
                        {{ $t('missionForm.tooltips.name') }}
                      </span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>
              </v-layout>
            </v-card>
          </v-flex>

          <!-- mission games -->
          <v-card-title class="justify-center cardWidth mt-4">
            <h2 class="font-weight-light">
              {{ $t('missionForm.games') }}
            </h2>
          </v-card-title>

          <draggable
            v-model="games"
            :disabled="!isDragButtonClicked"
            :scrollSensitivity="190"
            :force-fallback="true"
            draggable=".gameToDrag"
            @start="startDrag"
            @end="stopDrag"
          >
            <game-form-card
              v-for="(gameId, index) in games"
              :class="'gameCardClass gameToDrag'"
              :key="index"
              :orderGame="index + 1"
              :isDrag="drag"
              :gameId="gameId"
              :langEdition="langEdition"
              :numberGames="games.length"
              :missionId="missionId"
              :hasMissionGoToGame="hasGoToGame"
              :langsEdited="path.langsEdited"
              :pathGeolocation="path.geolocation"
              :pathId="pathId"
              :showHint="showHint"
              @drag-button-click="dragButtonClick"
              @update-game-content="updateGameContent"
              @update-geolocation="updateGeolocation"
              @delete-geolocation="deleteGeolocation"
              @delete-game="deleteGame"
              @valid-game-form="changeValidGameValue"
              @uploading-media="uploadingMedia"
            >
            </game-form-card>
          </draggable>

          <v-layout justify-center>
            <v-card
              v-if="!isMaxGames"
              class="addGameCard pointerClass"
              color="primary"
              @click="addGame"
            >
              <v-flex layout row align-center justify-center pr-2 pb-2 pt-2>
                <v-icon medium color="white" class="mr-2"
                  >mdi-plus-circle-outline</v-icon
                >
                <span class="white--text">
                  {{ $t('missionForm.addGame') }}
                </span>
              </v-flex>
            </v-card>
          </v-layout>

          <!-- mission rewards -->
          <v-card-title class="justify-center cardWidth">
            <h2 class="font-weight-light">
              {{ $t('missionForm.rewards') }}
            </h2>
          </v-card-title>

          <reward-card
            v-for="(reward, index) in rewards"
            :key="index"
            :class="'gameCardClass'"
            :reward="reward"
            :rewardId="index"
            :isLast="index === rewards.length - 1"
            :missionId="missionId"
            :pathId="pathId"
            :missionName="mission.name[langEdition]"
            :langsEdited="path.langsEdited"
            :langEdition="langEdition"
            :showHint="showHint"
            :isReadOnly="isReadOnly"
            :hasMissionXPReward="hasMissionXPReward"
            :hasMissionOtherReward="hasMissionOtherReward"
            @update-reward="updateRewardContent"
            @delete-reward="deleteReward"
            @uploading-media="uploadingMedia"
            @valid-reward-form="changeValidRewardValue"
          ></reward-card>
          <v-layout justify-center v-if="canAddReward && !isReadOnly">
            <v-card
              class="addGameCard pointerClass"
              color="primary"
              @click="
                access.includes('rewards')
                  ? addReward()
                  : (openPremiumDialog = true)
              "
            >
              <v-flex layout row align-center justify-center pr-2 pb-2 pt-2>
                <v-icon medium color="white" class="mr-2"
                  >mdi-plus-circle-outline</v-icon
                >
                <span class="white--text">
                  {{ $t('missionForm.addReward') }}
                </span>
              </v-flex>
            </v-card>
            <!--v-icon small color="yellow darken-2">mdi-star</v-icon-->
          </v-layout>
        </div>
      </v-layout>
    </v-form>

    <premium-dialog
      v-if="openPremiumDialog"
      :isTextDialogOpen="openPremiumDialog"
      :title="premiumTitle"
      @confirm="closePremium()"
      @cancel="closePremium()"
    >
    </premium-dialog>
  </v-container>
</template>

<script>
import SettingsBar from '../components/SettingsBar.vue';
import ImageInput from './ImageInput';
import GameFormCard from './GameFormCard';
import RewardCard from './RewardCard';
import PremiumDialog from './PremiumDialog';
import Draggable from 'vuedraggable';
import LanguageTranslator from '../components/LanguageTranslator.vue';

export default {
  name: 'MissionForm',
  components: {
    GameFormCard,
    RewardCard,
    ImageInput,
    SettingsBar,
    Draggable,
    PremiumDialog,
    LanguageTranslator,
  },
  props: {
    mission: {
      type: Object,
      default: null,
    },
    missionId: {
      type: String,
      default: null,
    },
    pathId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      langEdition: null,
      drag: false,
      name: { fr: null, en: null },
      imageObject: { author: '', url: '', legend: { fr: '', en: '' } },
      isComplete: null,
      isReadOnly: false,
      geolocation: null,

      //newReward:this.$t('upcomingFeature.newReward'),
      //rewardFeatureType: 'card',
      newMissionLocation: this.$t('upcomingFeature.newMissionLocation'),
      missionLocationFeatureType: 'locationButton',
      isDragButtonClicked: false,
      validMissionForm: true,
      validGames: null,
      validRewards: null,
      maxGames: 8,
      maxCharName: 50,
      openPremiumDialog: false,
      premiumTitle: this.$t('premiumDialog.title.rewards'),
      rules: {
        name: [
          (name) => Boolean(name) || this.$t('missionForm.rules.name.empty'),
          (name) =>
            (name && name.length <= this.maxCharName) ||
            this.$tc('missionForm.rules.name.length', this.maxCharName),
        ],
        image: this.$t('missionForm.rules.image'),
      },
      showHint: false,
    };
  },
  computed: {
    // games() {
    //   console.log('games',this.$store.getters['missions/getMissionGames'](this.missionId));
    //   return this.$store.getters['missions/getMissionGames'](this.missionId);
    // },
    access() {
      return this.$store.getters['paths/getPathsAccess'][this.pathId] || [];
    },
    gamesType() {
      return this.$store.getters['missions/getMissionGamesTypes'](
        this.missionId
      );
    },
    findGoToGame() {
      return this.gamesType.find((g) => g === 'goTo');
    },
    hasGoToGame() {
      return this.findGoToGame ? true : false;
    },
    rewards() {
      return this.mission && this.mission.rewards || [];
    },
    hasMissionXPReward() {
      return this.rewards.find((r) => r.type === 'xp') ? true : false;
    },
    hasMissionOtherReward() {
      return this.rewards.find(
        (r) => r.type === 'object' || r.type === 'infosheet'
      )
        ? true
        : false;
    },
    canAddReward() {
      return !this.hasMissionXPReward || !this.hasMissionOtherReward;
    },
    isMaxGames() {
      return this.games.length >= this.maxGames;
    },
    isFirstGame() {
      return this.games.length === 1;
    },
    invalidGamesForms() {
      if (!this.validGames) return [];
      return this.validGames
        .filter((game) => game.value === false)
        .map((game) => game.gameId)
        .map((gameId) => parseInt(this.games.indexOf(gameId)) + 1);
    },
    invalidRewardsForms() {
      console.log('invalidRewardsForms');
      if (!this.validRewards) return [];
      return this.validRewards
        .filter((reward) => reward.value === false)
        .map((reward) => reward.rewardId);
    },
    locale() {
      return this.$i18n.locale;
    },
    validMissionInformations() {
      if (
        !this.imageObject ||
        !this.imageObject.url ||
        this.imageObject.url.length === 0
      ) {
        return false;
      }
      return this.validMissionForm;
    },
    validMissionGamesRewards() {
      return (
        this.invalidGamesForms.length === 0 &&
        this.invalidRewardsForms.length === 0 &&
        this.validMissionInformations
      );
    },
    games: {
      //dragGames:{
      get() {
        console.log(
          'get games',
          this.$store.getters['missions/getMissionGames'](this.missionId)
        );
        return this.$store.getters['missions/getMissionGames'](this.missionId);
        // return JSON.parse(JSON.stringify(this.games));
      },
      async set(value) {
        let newGamesList = value.map((gameId) => this.gameInformations(gameId)); //récupère les informations de chaque jeu
        let missionPayLoad = { games: newGamesList };
        this.saveMission(missionPayLoad);
      },
    },
    userPaths() {
      return this.$store.state.paths.paths;
    },
    userPathsIds() {
      return Object.keys(this.userPaths);
    },
    statsPaths() {
      return this.$store.state.stats.statsPaths;
    },
    statsPathsIds() {
      return Object.keys(this.statsPaths);
    },
    path() {
      if (this.pathId) {
        return this.$store.getters['paths/getPathById'](this.pathId);
      }
      return null;
    },
  },
  created() {
    this.langEdition = this.locale;
    const mission = this.mission;

    this.path.langsEdited.forEach((lang) => {
      this.name[lang] = mission.name[lang];
    });
    this.imageObject = mission.pictures && mission.pictures[0];
    //  && mission.pictures[0] && mission.pictures[0].url;
    this.geolocation = mission.geolocation;
    //this.imageSourceUrl = mission.pictures && mission.pictures[0] && mission.pictures[0].sourceUrl;
    //this.imageAuthor = mission.pictures && mission.pictures[0] && mission.pictures[0].author;
    //this.imageLegend = mission.pictures && mission.pictures[0] && mission.pictures[0].legend;
    //this.rewards = mission.rewards;
    this.isComplete = Boolean(mission.isComplete);

    this.validGames = this.games.map((gameId) => {
      return { gameId: gameId, value: false };
    });
    this.validRewards = this.rewards.map((r, i) => {
      return { rewardId: i, value: false };
    });

    // Helpscout text btn & articles suggest
    window.Beacon('suggest', ['6310b491c713d51da3edac11']);
    window.Beacon('config', {
      display: {
        style: 'iconAndText',
        text: 'Les différents types de jeux',
        iconImage: 'buoy',
      },
    });
  },
  methods: {
    onImageChange(imageUrl) {
      this.imageUrl = imageUrl;
    },
    updateName() {
      if (
        this.name[this.langEdition] &&
        this.name[this.langEdition].length <= this.maxCharName &&
        this.name[this.langEdition] !== this.mission.name[this.langEdition]
      ) {
        let missionPayload = {
          name: { [this.langEdition]: this.name[this.langEdition] },
        };
        this.saveMission(missionPayload);
      }
    },
    updateImage(pictureObject) {
      // this.imageUrl=url;
      // let missionPayload={pictures: [{ url: this.imageUrl }]};
      this.imageObject = pictureObject;
      let missionPayload = { pictures: [pictureObject] };
      this.saveMission(missionPayload);
    },
    updateGeolocation(location) {
      this.geolocation = location;
      let missionPayload = { geolocation: this.geolocation };
      this.saveMission(missionPayload);
    },
    deleteGeolocation() {
      this.geolocation = null;
      let missionPayload = { geolocation: this.geolocation };
      this.saveMission(missionPayload);
    },
    updateIsComplete(value) {
      if (this.mission.isComplete[this.langEdition] !== Boolean(value)) {
        let missionPayload = {
          isComplete: { [this.langEdition]: Boolean(value) },
        };
        console.log('update iscomplete');
        this.saveMission(missionPayload);
      }
    },
    gameInformations(gameId) {
      return this.$store.getters['missions/getGameById'](gameId);
    },

    addGame() {
      let listGames = this.games.map((gameId) => this.gameInformations(gameId));
      listGames = [...listGames, { gameId: '' }];
      let missionPayLoad = { games: listGames };
      this.saveMission(missionPayLoad);
    },
    updateGameContent(newGameContent, gameToChangeId) {
      let newGamesList = this.games.map((gameId) => {
        if (gameId === gameToChangeId) {
          return newGameContent;
        }
        return this.gameInformations(gameId);
      });
      let missionPayload = { games: newGamesList };
      console.log('update game content');
      this.saveMission(missionPayload);
    },
    deleteGame(gameToDeleteId) {
      let missionPayLoad = {}
      // delete mission geoloc if game geoloc deleted
      const deleteGameType = this.gameInformations(gameToDeleteId).type
      if (deleteGameType==='goTo') missionPayLoad.geolocation = null

      let newListGames = JSON.parse(JSON.stringify(this.games))
        .filter((gameId) => gameId !== gameToDeleteId)
        .map((gameId) => this.gameInformations(gameId));
      missionPayLoad.games = newListGames
      
      this.saveMission(missionPayLoad);
    },
    addReward() {
      let newReward = {
        type: '',
        value: '',
        message: { fr: '', en: '' },
      };
      let missionPayLoad = {};
      if (this.rewards)
        missionPayLoad = { rewards: [...this.rewards, newReward] };
      else missionPayLoad = { rewards: [newReward] };
      this.saveMission(missionPayLoad);
    },
    updateRewardContent(newRewardContent, rewardToChangeId) {
      let newRewardsList = this.rewards.map((r, index) => {
        if (index === rewardToChangeId) {
          return newRewardContent;
        }
        return this.mission.rewards[index];
      });
      console.log('newRewardsList', newRewardsList);
      let missionPayload = { rewards: newRewardsList };
      this.saveMission(missionPayload);

      //
    },
    deleteReward(rewardToDeleteId) {
      console.log('delete reward', rewardToDeleteId);
      let newListRewards = JSON.parse(JSON.stringify(this.rewards)).filter(
        (r, index) => index !== rewardToDeleteId
      );
      console.log('newListRewards', newListRewards);
      let missionPayLoad = { rewards: newListRewards };
      this.saveMission(missionPayLoad);
    },
    closePremium() {
      this.openPremiumDialog = false;
    },

    startDrag() {
      this.drag = true;
    },
    stopDrag() {
      this.drag = false;
      this.isDragButtonClicked = false;
    },
    dragButtonClick() {
      this.isDragButtonClicked = true;
    },
    uploadingMedia(isUploading, percentProgress) {
      this.$emit('uploading-media', isUploading, percentProgress);
    },
    updateValidGames() {
      if (this.games.length > this.validGames.length) {
        //un jeu rajouté
        let validGamesIds = this.validGames.map((game) => game.gameId);
        this.games.forEach((gameId) => {
          if (!validGamesIds.includes(gameId)) {
            this.validGames = [
              ...this.validGames,
              { gameId: gameId, value: false },
            ];
          }
        });
      } else if (this.games.length < this.validGames.length) {
        //un jeu supprimé
        this.validGames = this.validGames.filter((game) =>
          this.games.includes(game.gameId)
        );
      }
    },
    updateValidRewards() {
      console.log(
        'updateValidRewards',
        this.rewards.length,
        this.validRewards.length
      );
      if (this.rewards.length > this.validRewards.length) {
        //un reward rajouté
        let validRewardsIds = this.validRewards.map(
          (reward) => reward.rewardId
        );
        this.rewards.forEach((r, i) => {
          console.log('reward', i);
          console.log(
            'validRewardsIds',
            validRewardsIds,
            !validRewardsIds.includes(i)
          );
          if (!validRewardsIds.includes(i)) {
            this.validRewards = [
              ...this.validRewards,
              { rewardId: i, value: false },
            ];
          }
        });
      }
      //else if(this.rewards.length<this.validRewards.length) {//un jeu supprimé
      //  this.validRewards=this.validRewards.find((r,i) => this.rewards.includes(reward.rewardId));
      //}
    },
    goToPath() {
      this.$emit('go-to-path');
    },
    changeValidGameValue(value, gameId) {
      let newValidGames = this.validGames.map((game) => {
        if (game.gameId === gameId) {
          let newGameValue = game;
          newGameValue.value = value;
          return newGameValue;
        }
        return game;
      });
    },
    changeValidRewardValue(value, rewardId) {
      let newValidGames = this.validRewards.map((reward) => {
        if (reward.rewardId === rewardId) {
          let newRewardValue = reward;
          newRewardValue.value = value;
          return newRewardValue;
        }
        return reward;
      });
    },
    async saveMission(missionPayLoad) {
      console.log('save missionpayload', missionPayLoad);
      try {
        this.$store.commit('app/SET_LOADING');
        await this.$store.dispatch('missions/UPDATE_MISSION', {
          missionId: this.missionId,
          missionPayload: missionPayLoad,
          index: 'clients',
          kuzzle: this.$kuzzle,
        });
        this.$store.commit('app/UNSET_LOADING');
      } catch (error) {
        console.log('error', error)
        // this.$toast.show(this.$t('missionForm.name'));
        this.$toast.show(JSON.stringify(error));
      }
      
    },

    async deleteMission() {
      try {
        await this.$store.commit('app/SET_LOADING');

        let pathsWithMission = this.userPathsIds.filter((pathId) =>
          this.missionsIdsByPathId(pathId).includes(this.missionId)
        );
        console.log('mission form delete mission pathsWithMission', pathsWithMission);
        await pathsWithMission.forEach((pathId) => {
          //suppression des données de la mission dans les parcours ou elle est présente
          this.removeMissionOfPath(pathId);
          //suppression des statistiques de la mission dans les parcours ou elle est présente
          this.removeMissionOfStat(pathId);
        });

        //il faudra supprimer la mission dans les linked missions des infosheets

        await this.$store.dispatch('missions/DELETE_MISSION', {
          missionId: this.missionId,
          index: this.$store.getters['auth/currentUserIndex'],
          kuzzle: this.$kuzzle,
        });
      } catch (error) {
        console.error(error.message);
      }
      await this.$store.commit('app/UNSET_LOADING');
      this.$emit('go-to-path');
    },
    async removeMissionOfPath(pathId) {
      try {
        await this.$store.dispatch('paths/REMOVE_MISSION', {
          kuzzle: this.$kuzzle,
          index: this.$store.getters['auth/currentUserIndex'],
          pathId: pathId,
          missionId: this.missionId,
          indexKey: null,
        });
      } catch (error) {
        console.error(error.message);
        this.$toast.show(
          this.$t('pathManagement.errors.removeMission'),
          this.$t('common.error')
        );
      }
    },
    async removeMissionOfStat(pathId) {
      console.log('pathId', pathId);
      console.log('statsPaths', this.statsPaths[pathId]);
      if (!this.statsPaths[pathId]) {
        return;
      }
      try {
        await this.$store.dispatch('stats/REMOVE_MISSION', {
          kuzzle: this.$kuzzle,
          index: this.$store.getters['auth/currentUserIndex'],
          pathId: pathId,
          missionId: this.missionId,
        });
      } catch (error) {
        console.error(error.message);
        this.$toast.show(
          this.$t('pathManagement.errors.removeMission'),
          this.$t('common.error')
        );
      }
    },
    missionsIdsByPathId(pathId) {
      if (
        this.userPaths[pathId] &&
        this.userPaths[pathId].missions &&
        this.userPaths[pathId].missions.length > 0
      ) {
        let pathMissionsIds = this.userPaths[pathId].missions.map(
          (mission) => mission.missionId
        );
        return pathMissionsIds;
      }
      return [];
    },
    changeLangEdition(lang) {
      this.langEdition = lang;
    },
    async updateTranslatedText(textTranslated, type) {
      if (textTranslated) {
        if (type === 'name') {
          this.name[this.langEdition] = textTranslated;
          this.updateName();
        }
      }
    },
    changeHint() {
      this.showHint = !this.showHint;
    },
  },

  watch: {
    validMissionGamesRewards(newValue, oldValue) {
      // 2 events simultanés
      // à revoir
      setTimeout(() => this.updateIsComplete(Boolean(newValue)), 1300); //sinon deux actions simultanées sur elasticsearch
      return newValue;
    },
    games(newValue, oldValue) {
      this.updateValidGames();
      return newValue;
    },
    rewards(newValue, oldValue) {
      this.updateValidRewards();
      return newValue;
    },
    validGames(newValue) {
      return newValue;
    },
    mission(newValue) {
      return newValue;
    },
    langEdition(newValue) {
      this.$refs.missionForm.resetValidation();
      return newValue;
    },
  },
};
</script>

<style lang="sass">


.cardWidth
  width: 100%

.layoutHeight
  height: 35vh
.formWidth
  width: 300px
.rulesText
  color: red;
  font-size: small;
  line-height: 120%;
  position: absolute;
  padding-top: 3px;

.addGameCard
  margin-top: 15px;
  margin-bottom: 15px;
  width: 200px

.gameCardClass
  margin-bottom: 15px;

.readOnlyClass
  pointer-events: none;

.stickyClass
  position: -webkit-sticky;
  position: sticky;
  top: 100px;
  width: 130px;
  height: 0px;
  z-index: 100
</style>
